import React, { FC, useState, useRef, DragEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faFile } from '@fortawesome/free-solid-svg-icons';
import { localizationService } from 'src/services';
import './fileDragDrop.scss';

interface FileDragDropProps {
	onFileSelect: (files: FileList) => void;
	acceptedFileTypes: string;
	maxFileSize: string;
	multiple?: boolean;
	disabled?: boolean;
	className?: string;
}

const FileDragDrop: FC<FileDragDropProps> = ({
	onFileSelect,
	acceptedFileTypes,
	maxFileSize,
	multiple = true,
	disabled = false,
	className = ''
}) => {
	const [isDragActive, setIsDragActive] = useState(false);
	const [isDragReject, setIsDragReject] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const validateFileTypes = (files: FileList): boolean => {
		const allowedExtensions = acceptedFileTypes
			.replace(/[.]/g, '')
			.toLowerCase()
			.split(',');

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			const fileExtension = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();

			if (!allowedExtensions.includes(fileExtension)) {
				return false;
			}
		}
		return true;
	};

	const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (disabled) return;

		setIsDragActive(true);

		// Check if dragged items contain files
		const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
		if (!hasFiles) {
			setIsDragReject(true);
		}
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		// Only set drag active to false if we're leaving the drop zone entirely
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setIsDragActive(false);
			setIsDragReject(false);
		}
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		setIsDragActive(false);
		setIsDragReject(false);

		if (disabled) return;

		const files = e.dataTransfer.files;

		if (files.length > 0) {
			if (!multiple && files.length > 1) {
				// Handle single file only case
				return;
			}

			if (validateFileTypes(files)) {
				onFileSelect(files);
			}
		}
	};

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) {
			onFileSelect(files);
		}
		// Reset input value to allow selecting the same file again
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleClick = () => {
		if (!disabled && fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const getDropZoneClass = () => {
		let classes = 'file-drag-drop-zone';

		if (isDragActive && !isDragReject) {
			classes += ' drag-active';
		} else if (isDragReject) {
			classes += ' drag-reject';
		}

		if (disabled) {
			classes += ' disabled';
		}

		return classes;
	};

	return (
		<div className={`file-drag-drop-container ${className}`}>
			<div
				className={getDropZoneClass()}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onClick={handleClick}
				role="button"
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleClick();
					}
				}}>
				<div className="drop-zone-content">
					<FontAwesomeIcon icon={faCloudUploadAlt} className="upload-icon" size="3x" />
					<div className="drop-zone-text">
						<p className="primary-text">
							{isDragActive && !isDragReject
								? localizationService.getLocalizedString('ipp.attachments.dragAndDrop.dropFilesHere')
								: localizationService.getLocalizedString('ipp.attachments.dragAndDrop.dragFilesHere')}
						</p>
						<p className="secondary-text">
							{localizationService.getLocalizedString('ipp.attachments.dragAndDrop.orClickToSelect')}
						</p>
						<p className="file-info">
							{localizationService.getLocalizedString('ipp.attachments.dragAndDrop.supportedFormats')}:{' '}
							{acceptedFileTypes}
						</p>
					</div>
				</div>

				{isDragReject && (
					<div className="drag-reject-overlay">
						<FontAwesomeIcon icon={faFile} className="reject-icon" size="2x" />
						<p>{localizationService.getLocalizedString('ipp.attachments.dragAndDrop.invalidFileType')}</p>
					</div>
				)}
			</div>

			<input
				ref={fileInputRef}
				type="file"
				accept={acceptedFileTypes}
				multiple={multiple}
				onChange={handleFileInputChange}
				style={{ display: 'none' }}
				disabled={disabled}
			/>
		</div>
	);
};

export default FileDragDrop;